/**
 * Enhanced Real-Time Cache Service
 * Combines caching with real-time invalidation for instant updates
 */

import { DataChangeEvent, realTimeSyncService } from "./realTimeSync.service";

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
  key: string;
}

export interface CacheStats {
  size: number;
  hits: number;
  misses: number;
  entries: string[];
  invalidations: number;
}

class RealTimeCacheService {
  private cache = new Map<string, CacheEntry>();
  // Track in-flight fetch promises so concurrent requests for same key share one network call
  private inFlight = new Map<string, Promise<any>>();
  private stats: CacheStats = {
    size: 0,
    hits: 0,
    misses: 0,
    entries: [],
    invalidations: 0,
  };
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Subscribe to real-time updates for automatic cache invalidation
    this.initializeRealTimeSync();
  }

  /**
   * Initialize real-time sync integration
   */
  private initializeRealTimeSync() {
    realTimeSyncService.subscribe({
      id: "cache_invalidator",
      callback: (event: DataChangeEvent) => {
        this.handleRealTimeEvent(event);
      },
    });
  }

  /**
   * Handle real-time events for cache invalidation
   */
  private handleRealTimeEvent(event: DataChangeEvent) {
    console.log("🔄 Cache handling real-time event:", event);

    switch (event.type) {
      case "property":
        this.invalidatePropertyCache(event.entityId, event.data);
        break;
      case "room":
        this.invalidateRoomCache(event.entityId, event.data);
        break;
      case "client":
        this.invalidateClientCache(event.entityId, event.data);
        break;
      case "master-data":
        this.invalidateMasterDataCache(event.data?.subType);
        break;
      case "rmc":
        this.invalidateRmcCache(event.entityId);
        break;
    }
  }

  /**
   * Get data from cache or fetch using provided function
   */
  async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl: number = this.defaultTTL
  ): Promise<T> {
    // Check if key exists and is not expired
    if (this.cache.has(key)) {
      const entry = this.cache.get(key)!;
      if (Date.now() - entry.timestamp < entry.ttl) {
        this.stats.hits++;
        return entry.data as T;
      } else {
        // Expired, remove it
        this.cache.delete(key);
        this.updateStats();
      }
    }

    // If a fetch for this key is already in progress, return the same promise
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key)! as Promise<T>;
    }

    // Cache miss - initiate new fetch and store promise immediately to avoid duplicate calls
    this.stats.misses++;
    const fetchPromise = fetchFn()
      .then((data) => {
        this.set(key, data, ttl);
        this.inFlight.delete(key);
        return data;
      })
      .catch((error) => {
        this.inFlight.delete(key);
        console.error(`Error fetching data for key ${key}:`, error);
        throw error;
      });

    this.inFlight.set(key, fetchPromise);
    return fetchPromise;
  }

  /**
   * Set data in cache
   */
  set<T>(key: string, data: T, ttl: number = this.defaultTTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
      key,
    });
    this.updateStats();
  }

  /**
   * Get data from cache (without fetching)
   */
  get<T>(key: string): T | null {
    if (!this.cache.has(key)) {
      return null;
    }

    const entry = this.cache.get(key)!;
    if (Date.now() - entry.timestamp >= entry.ttl) {
      // Expired
      this.cache.delete(key);
      this.updateStats();
      return null;
    }

    this.stats.hits++;
    return entry.data as T;
  }

  /**
   * Invalidate specific cache key
   */
  invalidate(key: string) {
    const existed = this.cache.delete(key);
    if (existed) {
      this.stats.invalidations++;
      this.updateStats();
      console.log(`🗑️ Cache invalidated: ${key}`);
    }
  }

  /**
   * Invalidate cache by pattern
   */
  invalidateByPattern(pattern: RegExp) {
    let invalidatedCount = 0;
    this.cache.forEach((entry, key) => {
      if (pattern.test(key)) {
        this.cache.delete(key);
        invalidatedCount++;
      }
    });

    if (invalidatedCount > 0) {
      this.stats.invalidations += invalidatedCount;
      this.updateStats();
      console.log(
        `🗑️ Cache pattern invalidated: ${pattern} (${invalidatedCount} entries)`
      );
    }
  }

  /**
   * Invalidate property-related cache
   */
  private invalidatePropertyCache(propertyId: string | number, data?: any) {
    // Invalidate property-specific caches
    this.invalidateByPattern(new RegExp(`property.*${propertyId}`));
    this.invalidateByPattern(new RegExp(`room.*property.*${propertyId}`));

    // If property has rooms, invalidate those too
    if (data?.roomIds?.length) {
      data.roomIds.forEach((roomId: string | number) => {
        this.invalidateByPattern(new RegExp(`room.*${roomId}`));
      });
    }
  }

  /**
   * Invalidate room-related cache
   */
  private invalidateRoomCache(roomId: string | number, data?: any) {
    this.invalidateByPattern(new RegExp(`room.*${roomId}`));

    // Also invalidate property cache if room belongs to a property
    if (data?.propertyId) {
      this.invalidateByPattern(new RegExp(`property.*${data.propertyId}`));
    }
  }

  /**
   * Invalidate client-related cache
   */
  private invalidateClientCache(clientId: string | number, data?: any) {
    this.invalidateByPattern(new RegExp(`client.*${clientId}`));

    // Invalidate RMC-related client cache
    if (data?.rmcId) {
      this.invalidate(`CLIENT_BY_RMC_${data.rmcId}`);
    }
  }

  /**
   * Invalidate master data cache
   */
  private invalidateMasterDataCache(subType?: string) {
    if (subType) {
      switch (subType) {
        case "cities":
          this.invalidate("CITIES");
          break;
        case "room-types":
          this.invalidate("ROOM_TYPES");
          break;
        case "property-types":
          this.invalidate("PROPERTY_TYPES");
          break;
      }
    } else {
      // Invalidate all master data
      this.invalidate("CITIES");
      this.invalidate("ROOM_TYPES");
      this.invalidate("PROPERTY_TYPES");
    }
  }

  /**
   * Invalidate RMC-related cache
   */
  private invalidateRmcCache(rmcId: string | number) {
    this.invalidate("RMC_ALL");
    this.invalidateByPattern(new RegExp(`CLIENT_BY_RMC_${rmcId}`));
  }

  /**
   * Force refresh specific data
   */
  async forceRefresh<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
    this.invalidate(key);
    return this.getOrFetch(key, fetchFn);
  }

  /**
   * Update cache statistics
   */
  private updateStats() {
    this.stats.size = this.cache.size;
    this.stats.entries = Array.from(this.cache.keys());
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    this.updateStats();
    return { ...this.stats };
  }

  /**
   * Clear entire cache
   */
  clear() {
    this.cache.clear();
    this.stats = {
      size: 0,
      hits: 0,
      misses: 0,
      entries: [],
      invalidations: 0,
    };
    console.log("🗑️ Entire cache cleared");
  }

  /**
   * Cleanup expired entries
   */
  cleanupExpired() {
    const now = Date.now();
    let cleanedCount = 0;

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key);
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      this.updateStats();
      console.log(`🧹 Cleaned up ${cleanedCount} expired cache entries`);
    }
  }
}

// Export singleton instance
export const realTimeCacheService = new RealTimeCacheService();

// Cache keys constants (same as before for compatibility)
export const CACHE_KEYS = {
  CITIES: "CITIES",
  ROOM_TYPES: "ROOM_TYPES",
  PROPERTY_TYPES: "PROPERTY_TYPES",
  RMC_ALL: "RMC_ALL",
  RMC_LIST_ITEMS: "RMC_LIST_ITEMS",
  CLIENT_BY_RMC: "CLIENT_BY_RMC_",
  ZONES: "ZONES",
} as const;

// Auto-cleanup expired entries every 10 minutes
setInterval(() => {
  realTimeCacheService.cleanupExpired();
}, 10 * 60 * 1000);
